// @flow
import * as R from "ramda";
import type { HourBlock, Office } from "types";
import type { Moment } from "moment";

export const isOpen = R.curry((now: Moment, office: ?Office): boolean => {
	if (!office || !office.online) return false;
	if (!Array.isArray(office.hours)) return false;
	const weekday = now.weekday();
	const block: ?HourBlock = R.find(
		R.propEq("weekday", weekday),
		office.hours || []
	);
	if (R.isNil(block)) return false;
	// $FlowFixMe
	return now.isBetween(block.from, block.to);
});

const clean = R.compose(R.trim, R.toLower);
const defaultTrue = (arg: ?string, fn: (string) => boolean): boolean =>
	arg ? fn(arg) : true;

type FilterFn = (Office, ?string) => boolean;
const filters: { [string]: FilterFn } = {
	category: (office, cat) =>
		defaultTrue(cat, (cat) =>
			office.category ? office.category.slug === cat : false
		),
	district: (office, dist) =>
		defaultTrue(dist, (dist) => {
			if (office.location && office.location.district) {
				return clean(office.location.district) === dist;
			}
			return true;
		}),
};

const filterByQuery =
	(queryKey: string, queryVal: string) =>
	(office: Office): boolean => {
		const filterFn: ?FilterFn = filters[queryKey];
		if (filterFn) {
			return filterFn(office, queryVal);
		}
		return true;
	};

type Queries = { [string]: string };
export const filterByAllQueries =
	(queries: Queries) =>
	(office: Office): boolean =>
		R.allPass(
			R.map(([key, val]) => filterByQuery(key, val), R.toPairs(queries))
		)(office);

export const calcOfficeHours = (officeHours) => {
	let groups = officeHours
		.filter((oh) => oh.reservable)
		.sort((oh0, oh1) => oh0.weekday - oh1.weekday)
		.reduce((p, c) => {
			const key = `${c.from}-${c.to}`;
			p[key] = {
				from: c.from,
				to: c.to,
				weekdayFrom:
					p[key] && p[key].weekdayFrom
						? p[key].weekdayFrom
						: c.weekday,
				weekdayTo: c.weekday,
			};
			return p;
		}, {});

	return Object.values(groups).map((range) => {
		const { weekdayFrom, from, to, weekdayTo } = range;
		const date = `${days[weekdayFrom - 1]} a ${days[weekdayTo - 1]}`;
		const time = `desde las ${from.substr(0, 5)} a las ${to.substr(0, 5)}`;
		return date + time;
	});
};

/*
 * { weekdayFrom: 1, weekdayTo: 2, from: new Date(), to: new Date() }
 * */

const days = [
	"lunes",
	"martes",
	"miércoles",
	"jueves",
	"viernes",
	"sábado",
	"domingo",
];

export const getIsoCodeByTerritory = (countryCode) => {
	switch (countryCode) {
		case "Argentina":
			return "ar";
		case "Bolivia":
			return "bo";
		case "Brasil":
			return "br";
		case "Chile":
			return "cl";
		case "Colombia":
			return "co";
		case "Ecuador":
			return "ec";
		case "México":
			return "mx";
		case "Perú":
			return "pe";
		case "Uruguay":
			return "ur";
		default:
			return "cl";
	}
};
